import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { venueUrl } from "../../utils/venueUrl";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  ArrowLeft, Save, Loader2, ExternalLink, MapPin, Phone, Clock, ImagePlus,
  Trash2, Upload, ChevronUp, ChevronDown, CalendarOff, Plus, UtensilsCrossed, Edit, Megaphone,
  Facebook, Instagram, Info,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { BilingualInput, BilingualTextarea } from "../../components/BilingualField";
import VenueTagChipGroups from "../../components/VenueTagChipGroups";
import { useTranslation } from "react-i18next";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function AdminVenueEdit() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [venue, setVenue] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]); // areas filtered by selected district
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [venueRes, districtsRes, typesRes, cuisinesRes, usersRes] = await Promise.all([
          axios.get(`${API}/admin/venues/${venueId}`),
          axios.get(`${API}/taxonomy/districts`),
          axios.get(`${API}/taxonomy/venue-types`),
          axios.get(`${API}/taxonomy/cuisines`),
          axios.get(`${API}/admin/users`),
        ]);
        const v = venueRes.data;
        setVenue(v);
        setDistricts(districtsRes.data);
        setVenueTypes(typesRes.data);
        setCuisines(cuisinesRes.data);
        setUsers(usersRes.data.users || []);
        setForm({
          name: v.name || "", name_el: v.name_el || "",
          slug: v.slug || "",
          description_short: v.description_short || "", description_short_el: v.description_short_el || "",
          description_long: v.description_long || "", description_long_el: v.description_long_el || "",
          venue_type_id: v.venue_type_id || "", district_id: v.district_id || v.city_id || "", cuisine_id: v.cuisine_id || "",
          specialty_ids: Array.isArray(v.specialty_ids) ? v.specialty_ids : [],
          tag_ids: [],
          address: v.address || "", address_el: v.address_el || "",
          formatted_address: v.formatted_address || "", formatted_address_el: v.formatted_address_el || "",
          latitude: v.latitude || "", longitude: v.longitude || "",
          phone: v.phone || "", email: v.email || "", website: v.website || "",
          facebook_url: v.facebook_url || "", instagram_url: v.instagram_url || "",
          booking_mode: v.booking_mode || "instant_confirm",
          reservation_policy: v.reservation_policy || "", reservation_policy_el: v.reservation_policy_el || "",
          owner_user_id: v.owner_user_id || "",
          // Resolve the existing owner's email so the free-text field shows
          // the current owner. We pre-fetched `users` above. If the owner
          // isn't in that list (rare — admins always are, but a customer-
          // promoted owner might be), fallback to empty string and the
          // backend will keep the existing owner_user_id intact unless
          // admin types something different.
          owner_email: (usersRes.data.users || []).find((u) => u.user_id === v.owner_user_id)?.email || "",
          is_featured: v.is_featured || false, is_active: v.is_active !== false,
          bookings_enabled: v.bookings_enabled !== false,
          area_id: v.area_id || "",
        });
        // Fetch existing venue tag assignments
        try {
          const tagsRes = await axios.get(`${API}/admin/venues/${venueId}/tags`);
          setForm((f) => ({ ...f, tag_ids: tagsRes.data?.tag_ids || [] }));
        } catch { /* non-fatal */ }
      } catch {
        toast.error(t("admin_venue_edit:load_failed", { defaultValue: "Failed to load venue" }));
        navigate("/admin/venues");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [venueId, navigate, t]);

  // Reload areas whenever the selected district changes. Areas are scoped to a
  // single district in the taxonomy; the dropdown only offers areas for that district.
  useEffect(() => {
    const districtId = form?.district_id;
    if (!districtId) { setAreas([]); return; }
    let cancelled = false;
    axios.get(`${API}/taxonomy/areas`, { params: { district_id: districtId } })
      .then((r) => { if (!cancelled) setAreas(r.data || []); })
      .catch(() => { if (!cancelled) setAreas([]); });
    return () => { cancelled = true; };
  }, [form?.district_id]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      const optionalTextFields = ["phone", "email", "website", "reservation_policy", "reservation_policy_el",
        "description_short", "description_short_el", "description_long", "description_long_el",
        "formatted_address", "formatted_address_el", "address_el", "name_el",
        "facebook_url", "instagram_url"];
      for (const [k, v] of Object.entries(form)) {
        if (k === "latitude" || k === "longitude") {
          if (v !== "" && v !== null) payload[k] = parseFloat(v);
        } else if (k === "specialty_ids") {
          payload[k] = Array.isArray(v) ? v : [];
        } else if (k === "tag_ids") {
          // tag_ids are not part of the venue document — saved via separate endpoint below
          continue;
        } else if (k === "owner_user_id") {
          // Owner is now resolved server-side from `owner_email`, so we
          // never send a stale `owner_user_id` from the form. The backend
          // resolver returns 400 with a clear message if the typed email
          // doesn't exist, instead of silently overwriting with the old id.
          continue;
        } else if (k === "owner_email") {
          // Always send the email so an empty string explicitly detaches.
          payload[k] = (v || "").trim().toLowerCase();
        } else if (optionalTextFields.includes(k)) {
          payload[k] = v || "";
        } else if (v !== "" && v !== null) {
          payload[k] = v;
        }
      }
      await axios.put(`${API}/admin/venues/${venueId}`, payload);
      // Persist venue tag assignments separately (replaces all tag rows for this venue)
      await axios.put(`${API}/admin/venues/${venueId}/tags`, { tag_ids: form.tag_ids || [] });
      toast.success(t("admin_venue_edit:update_success", { defaultValue: "Venue updated successfully!" }));
    } catch (error) {
      toast.error(formatApiErrorDetail(error) || t("admin_venue_edit:update_failed", { defaultValue: "Failed to update venue" }));
    } finally {
      setSaving(false);
    }
  };

  const handleMapMove = useCallback((lat, lng) => {
    setForm(f => ({ ...f, latitude: lat.toFixed(6), longitude: lng.toFixed(6) }));
  }, []);

  // Instant-toggle: flip `bookings_enabled` without requiring a Save-Changes click.
  // We optimistically update local state and PATCH the backend; on failure revert.
  const [togglingBookings, setTogglingBookings] = useState(false);
  const handleToggleBookings = async (next) => {
    setTogglingBookings(true);
    const prev = form.bookings_enabled;
    setForm((f) => ({ ...f, bookings_enabled: next }));
    try {
      await axios.put(`${API}/admin/venues/${venueId}`, { bookings_enabled: next });
      toast.success(next
        ? t("admin_venue_edit:bookings_enabled_toast", { defaultValue: "Bookings enabled" })
        : t("admin_venue_edit:bookings_disabled_toast", { defaultValue: "Bookings disabled" }));
    } catch {
      setForm((f) => ({ ...f, bookings_enabled: prev }));
      toast.error(t("admin_venue_edit:bookings_toggle_failed", { defaultValue: "Failed to update bookings status" }));
    } finally {
      setTogglingBookings(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="space-y-6" data-testid="venue-edit-loading">
        <div className="animate-pulse bg-muted h-10 w-48 rounded-lg" />
        <div className="animate-pulse bg-muted h-[600px] rounded-xl" />
      </div>
    );
  }

  const localizedName = (item) => {
    if (!item) return "";
    return (lang === "el" && (item.name_el || item.label_el)) || item.name || item.label || "";
  };
  const typeName = localizedName(venueTypes.find(t => t.id === form.venue_type_id));
  const districtName = localizedName(districts.find(d => d.id === form.district_id));

  return (
    <div data-testid="admin-venue-edit-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin/venues")} data-testid="venue-edit-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> {t("admin_venue_edit:back", { defaultValue: "Back" })}
          </Button>
          <div>
            <h1 className="text-2xl font-medium text-foreground">{(lang === "el" && venue.name_el) || venue.name}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {typeName}{districtName ? ` \u2022 ${districtName}` : ""}{venue.slug ? ` \u2022 /${venue.slug}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to={venueUrl(venue)} target="_blank" rel="noopener noreferrer" data-testid="venue-edit-preview">
              <ExternalLink className="h-4 w-4 mr-1" /> {t("admin_venue_edit:preview", { defaultValue: "Preview" })}
            </Link>
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="venue-edit-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {saving ? t("admin_venue_edit:saving", { defaultValue: "Saving..." }) : t("admin_venue_edit:save_changes", { defaultValue: "Save Changes" })}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} data-testid="edit-venue-form">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_basic", { defaultValue: "Basic Information" })}
              </h2>
              <div className="space-y-4">
                <BilingualInput label={t("admin_venue_edit:field_name", { defaultValue: "Venue Name" }) + " *"} value={form.name} valueEl={form.name_el} onChange={(e) => setForm({ ...form, name: e.target.value })} onChangeEl={(e) => setForm({ ...form, name_el: e.target.value })} data-testid="edit-venue-name" />
                <div><Label>{t("admin_venue_edit:field_slug", { defaultValue: "URL Slug" })}</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="mt-1 font-mono text-sm" data-testid="edit-venue-slug" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin_venue_edit:field_venue_type", { defaultValue: "Venue Type" })}</Label><Select value={form.venue_type_id} onValueChange={(v) => setForm({ ...form, venue_type_id: v })}><SelectTrigger className="mt-1" data-testid="edit-venue-type"><SelectValue placeholder={t("admin_venue_edit:select_type", { defaultValue: "Select type" })} /></SelectTrigger><SelectContent>{venueTypes.map((vt) => <SelectItem key={vt.id} value={vt.id}>{localizedName(vt)}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>{t("admin_venue_edit:field_city", { defaultValue: "District" })}</Label><Select value={form.district_id} onValueChange={(v) => setForm({ ...form, district_id: v, area_id: "" })}><SelectTrigger className="mt-1" data-testid="edit-venue-district"><SelectValue placeholder={t("admin_venue_edit:select_city", { defaultValue: "Select district" })} /></SelectTrigger><SelectContent>{districts.map((d) => <SelectItem key={d.id} value={d.id}>{localizedName(d)}</SelectItem>)}</SelectContent></Select></div>
                </div>
                {/* Tag chip groups (Cuisine / Music / Mood / Amenity / Feature) — driven by Tag Groups taxonomy */}
                {(() => {
                  const selectedTypeSlug = venueTypes.find(vt => vt.id === form.venue_type_id)?.slug || "";
                  const toggleTag = (id) => {
                    const current = Array.isArray(form.tag_ids) ? form.tag_ids : [];
                    setForm({
                      ...form,
                      tag_ids: current.includes(id) ? current.filter(x => x !== id) : [...current, id],
                    });
                  };
                  return (
                    <VenueTagChipGroups
                      venueTypeSlug={selectedTypeSlug}
                      tagIds={form.tag_ids}
                      onTagToggle={toggleTag}
                    />
                  );
                })()}
                <BilingualTextarea label={t("admin_venue_edit:field_short_description", { defaultValue: "Short Description" })} value={form.description_short} valueEl={form.description_short_el} onChange={(e) => setForm({ ...form, description_short: e.target.value })} onChangeEl={(e) => setForm({ ...form, description_short_el: e.target.value })} rows={2} data-testid="edit-venue-desc-short" />
                <BilingualTextarea label={t("admin_venue_edit:field_full_description", { defaultValue: "Full Description" })} value={form.description_long} valueEl={form.description_long_el} onChange={(e) => setForm({ ...form, description_long: e.target.value })} onChangeEl={(e) => setForm({ ...form, description_long_el: e.target.value })} rows={5} data-testid="edit-venue-desc-long" />
              </div>
            </section>

            <OpeningHoursEditor venueId={venueId} />
            <GalleryManager venueId={venueId} />
            <MenuManager venueId={venueId} />

            {/* Location with Map */}
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_location", { defaultValue: "Location" })}
              </h2>
              <div className="space-y-4">
                <BilingualInput label={t("admin_venue_edit:field_street_address", { defaultValue: "Street Address" })} value={form.address} valueEl={form.address_el} onChange={(e) => setForm({ ...form, address: e.target.value })} onChangeEl={(e) => setForm({ ...form, address_el: e.target.value })} data-testid="edit-venue-address" />
                <BilingualInput label={t("admin_venue_edit:field_formatted_address", { defaultValue: "Formatted Display Address" })} value={form.formatted_address} valueEl={form.formatted_address_el} onChange={(e) => setForm({ ...form, formatted_address: e.target.value })} onChangeEl={(e) => setForm({ ...form, formatted_address_el: e.target.value })} placeholder={t("admin_venue_edit:formatted_address_placeholder", { defaultValue: "Full display address" })} />
                <div>
                  <Label>{t("admin_venue_edit:field_area", { defaultValue: "Area" })}</Label>
                  <Select
                    value={form.area_id || "__none__"}
                    onValueChange={(v) => setForm({ ...form, area_id: v === "__none__" ? "" : v })}
                    disabled={!form.district_id}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-venue-area">
                      <SelectValue placeholder={
                        !form.district_id
                          ? t("admin_venue_edit:area_pick_city_first", { defaultValue: "Pick a district first" })
                          : areas.length === 0
                            ? t("admin_venue_edit:area_none_for_city", { defaultValue: "No areas defined for this district" })
                            : t("admin_venue_edit:select_area", { defaultValue: "Select area (optional)" })
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("admin_venue_edit:no_area", { defaultValue: "— No area —" })}</SelectItem>
                      {areas.map((a) => (
                        <SelectItem key={a.area_id || a.id} value={a.area_id || a.id}>{localizedName(a)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>{t("admin_venue_edit:field_latitude", { defaultValue: "Latitude" })}</Label><Input type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="mt-1" data-testid="edit-venue-lat" /></div>
                  <div><Label>{t("admin_venue_edit:field_longitude", { defaultValue: "Longitude" })}</Label><Input type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="mt-1" data-testid="edit-venue-lng" /></div>
                </div>
                <DraggableMap lat={parseFloat(form.latitude) || 35.1856} lng={parseFloat(form.longitude) || 33.3823} onMove={handleMapMove} />
                <p className="text-xs text-muted-foreground">{t("admin_venue_edit:map_hint", { defaultValue: "Drag the pin to set the venue location. Coordinates update automatically." })}</p>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4 flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_contact", { defaultValue: "Contact Information" })}</h2>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>{t("admin_venue_edit:field_phone", { defaultValue: "Phone" })}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="+357..." data-testid="edit-venue-phone" /></div>
                <div><Label>{t("admin_venue_edit:field_email", { defaultValue: "Email" })}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="edit-venue-email" /></div>
                <div><Label>{t("admin_venue_edit:field_website", { defaultValue: "Website" })}</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1" placeholder="https://" data-testid="edit-venue-website" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="flex items-center gap-1.5"><Facebook className="h-4 w-4" /> {t("admin_venue_edit:field_facebook", { defaultValue: "Facebook URL" })}</Label>
                  <Input
                    type="url"
                    value={form.facebook_url}
                    onChange={(e) => setForm({ ...form, facebook_url: e.target.value })}
                    className="mt-1"
                    placeholder="https://www.facebook.com/yourpage"
                    data-testid="edit-venue-facebook"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5"><Instagram className="h-4 w-4" /> {t("admin_venue_edit:field_instagram", { defaultValue: "Instagram URL" })}</Label>
                  <Input
                    type="url"
                    value={form.instagram_url}
                    onChange={(e) => setForm({ ...form, instagram_url: e.target.value })}
                    className="mt-1"
                    placeholder="https://www.instagram.com/yourpage"
                    data-testid="edit-venue-instagram"
                  />
                </div>
              </div>
            </section>

            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">{t("admin_venue_edit:section_reservation_policy", { defaultValue: "Reservation Policy" })}</h2>
              <BilingualTextarea label="" value={form.reservation_policy} valueEl={form.reservation_policy_el} onChange={(e) => setForm({ ...form, reservation_policy: e.target.value })} onChangeEl={(e) => setForm({ ...form, reservation_policy_el: e.target.value })} rows={3} data-testid="edit-venue-policy" />
            </section>

            <AvailabilityOverrides venueId={venueId} />
            <PromotionsManager venueId={venueId} />
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <VenuePlanPanel venueId={venueId} />
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">{t("admin_venue_edit:section_status", { defaultValue: "Status" })}</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between"><Label>{t("admin_venue_edit:active", { defaultValue: "Active" })}</Label><Select value={form.is_active ? "true" : "false"} onValueChange={(v) => setForm({ ...form, is_active: v === "true" })}><SelectTrigger className="w-28" data-testid="edit-venue-active"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">{t("admin_venue_edit:active", { defaultValue: "Active" })}</SelectItem><SelectItem value="false">{t("admin_venue_edit:inactive", { defaultValue: "Inactive" })}</SelectItem></SelectContent></Select></div>
                <div className="flex items-center justify-between"><Label>{t("admin_venue_edit:featured", { defaultValue: "Featured" })}</Label><Select value={form.is_featured ? "true" : "false"} onValueChange={(v) => setForm({ ...form, is_featured: v === "true" })}><SelectTrigger className="w-28" data-testid="edit-venue-featured"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="true">{t("admin_venue_edit:yes", { defaultValue: "Yes" })}</SelectItem><SelectItem value="false">{t("admin_venue_edit:no", { defaultValue: "No" })}</SelectItem></SelectContent></Select></div>
                <div className="flex items-center justify-between" data-testid="admin-venue-bookings-toggle-wrap">
                  <Label htmlFor="admin-venue-bookings-toggle" className="cursor-pointer">
                    {t("admin_venue_edit:bookings_enabled", { defaultValue: "Bookings" })}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="admin-venue-bookings-toggle"
                      checked={!!form.bookings_enabled}
                      onCheckedChange={handleToggleBookings}
                      disabled={togglingBookings}
                      data-testid="admin-venue-bookings-toggle"
                    />
                    <span className={`text-xs font-medium w-8 ${form.bookings_enabled ? "text-emerald-600" : "text-rose-600"}`}>
                      {form.bookings_enabled ? t("common:on", { defaultValue: "On" }) : t("common:off", { defaultValue: "Off" })}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">{t("admin_venue_edit:section_booking_settings", { defaultValue: "Booking Settings" })}</h2>
              <Label>{t("admin_venue_edit:field_booking_mode", { defaultValue: "Booking Mode" })}</Label>
              <Select value={form.booking_mode} onValueChange={(v) => setForm({ ...form, booking_mode: v })}><SelectTrigger className="mt-1" data-testid="edit-venue-booking-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="instant_confirm">{t("admin_venue_edit:booking_mode_instant", { defaultValue: "Instant Confirm" })}</SelectItem><SelectItem value="manual_confirm">{t("admin_venue_edit:booking_mode_manual", { defaultValue: "Manual Confirm" })}</SelectItem><SelectItem value="request_only">{t("admin_venue_edit:booking_mode_request", { defaultValue: "Request Only" })}</SelectItem></SelectContent></Select>
            </section>
            <section className="bg-card rounded-xl border border-border p-6">
              <h2 className="text-lg font-medium text-foreground mb-4">{t("admin_venue_edit:section_owner", { defaultValue: "Owner" })}</h2>
              <Label>{t("admin_venue_edit:field_assigned_owner", { defaultValue: "Assigned Owner" })}</Label>
              {/* Free-text email input — the dropdown was removed because it
                  grew unboundedly with every new venue_owner / customer.
                  Backend resolves the typed email to a user_id (or returns
                  400 with a clear message if no match). Empty string clears
                  the owner. */}
              <Input
                type="email"
                value={form.owner_email || ""}
                onChange={(e) => setForm({ ...form, owner_email: e.target.value, owner_user_id: "" })}
                placeholder={t("admin_venue_edit:assign_owner_placeholder", { defaultValue: "owner@example.com (leave empty to detach)" })}
                className="mt-1"
                data-testid="edit-venue-owner"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("admin_venue_edit:assign_owner_hint", {
                  defaultValue: "Type the email of an existing TableCy user. They will be auto-promoted to venue_owner role.",
                })}
              </p>
            </section>
            <section className="bg-muted/50 rounded-xl border border-border p-6">
              <h2 className="text-sm font-medium text-muted-foreground mb-3">{t("admin_venue_edit:section_venue_info", { defaultValue: "Venue Info" })}</h2>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>{t("admin_venue_edit:id_label", { defaultValue: "ID" })}: <span className="font-mono text-xs">{venue.venue_id}</span></p>
                {venue.created_at && <p>{t("admin_venue_edit:created", { defaultValue: "Created" })}: {new Date(venue.created_at).toLocaleDateString(lang === "el" ? "el-GR" : "en-US")}</p>}
                {venue.average_review_score > 0 && <p>{t("admin_venue_edit:rating", { defaultValue: "Rating" })}: {venue.average_review_score.toFixed(1)} ({venue.review_count} {t("admin_venue_edit:reviews", { defaultValue: "reviews" })})</p>}
              </div>
            </section>
          </div>
        </div>
      </form>
    </div>
  );
}

/* ============== DRAGGABLE MAP ============== */
function DraggableMap({ lat, lng, onMove }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const L = (await import("leaflet")).default;
      if (!mounted || !containerRef.current || mapRef.current) return;
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });
      const map = L.map(containerRef.current).setView([lat, lng], 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);
      const marker = L.marker([lat, lng], { draggable: true }).addTo(map);
      marker.on("dragend", () => {
        const pos = marker.getLatLng();
        onMove(pos.lat, pos.lng);
      });
      map.on("click", (e) => {
        marker.setLatLng(e.latlng);
        onMove(e.latlng.lat, e.latlng.lng);
      });
      mapRef.current = map;
      markerRef.current = marker;
    };
    init();
    return () => { mounted = false; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  useEffect(() => {
    if (markerRef.current) {
      const pos = markerRef.current.getLatLng();
      if (Math.abs(pos.lat - lat) > 0.0001 || Math.abs(pos.lng - lng) > 0.0001) {
        markerRef.current.setLatLng([lat, lng]);
        mapRef.current?.setView([lat, lng], mapRef.current.getZoom());
      }
    }
  }, [lat, lng]);

  return <div ref={containerRef} className="h-[300px] rounded-lg border border-border z-0" data-testid="venue-map" />;
}

/* ============== OPENING HOURS ============== */
function OpeningHoursEditor({ venueId }) {
  const { t } = useTranslation();
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/venues/${venueId}/opening-hours`)
      .then((res) => setHours(res.data))
      .catch(() => setHours(DAY_KEYS.map((_, i) => ({ day_of_week: i, open_time: "09:00", close_time: "23:00", is_closed: false }))))
      .finally(() => setLoading(false));
  }, [venueId]);

  const updateDay = (idx, field, value) => setHours(hours.map((h, i) => i === idx ? { ...h, [field]: value } : h));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/venues/${venueId}/opening-hours`, { hours: hours.map(h => ({ day_of_week: h.day_of_week, open_time: h.open_time, close_time: h.close_time, is_closed: h.is_closed })) });
      setHours(res.data);
      toast.success(t("admin_venue_edit:hours_saved", { defaultValue: "Opening hours saved" }));
    } catch { toast.error(t("admin_venue_edit:hours_save_failed", { defaultValue: "Failed to save opening hours" })); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse bg-muted h-40 rounded-xl" />;
  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="opening-hours-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_opening_hours", { defaultValue: "Opening Hours" })}</h2>
        <Button size="sm" variant="outline" onClick={handleSave} disabled={saving} data-testid="save-opening-hours">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} {saving ? t("admin_venue_edit:saving", { defaultValue: "Saving..." }) : t("admin_venue_edit:save_hours", { defaultValue: "Save Hours" })}
        </Button>
      </div>
      <div className="space-y-2">
        {hours.map((h, idx) => (
          <div key={h.day_of_week} className="flex items-center gap-3" data-testid={`hours-day-${h.day_of_week}`}>
            <span className="w-24 text-sm font-medium text-foreground">{t(`admin_venue_edit.day_${DAY_KEYS[h.day_of_week]}`, { defaultValue: DAY_KEYS[h.day_of_week].charAt(0).toUpperCase() + DAY_KEYS[h.day_of_week].slice(1) })}</span>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={h.is_closed} onChange={(e) => updateDay(idx, "is_closed", e.target.checked)} className="rounded border-border" />
              <span className="text-xs text-muted-foreground">{t("admin_venue_edit:closed", { defaultValue: "Closed" })}</span>
            </label>
            {!h.is_closed ? (
              <>
                <Input type="time" value={h.open_time} onChange={(e) => updateDay(idx, "open_time", e.target.value)} className="w-32 h-8 text-sm" />
                <span className="text-muted-foreground text-sm">{t("admin_venue_edit:to", { defaultValue: "to" })}</span>
                <Input type="time" value={h.close_time} onChange={(e) => updateDay(idx, "close_time", e.target.value)} className="w-32 h-8 text-sm" />
              </>
            ) : <span className="text-sm text-muted-foreground italic">{t("admin_venue_edit:closed_all_day", { defaultValue: "Closed all day" })}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============== GALLERY WITH ALERTDIALOG + TOUCH REORDER ============== */
function GalleryManager({ venueId }) {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [settingCover, setSettingCover] = useState(null);
  const fileInputRef = useRef(null);

  const fetchPhotos = () => {
    axios.get(`${API}/admin/venues/${venueId}/gallery`)
      .then((res) => setPhotos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchPhotos(); }, [venueId]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/admin/venues/${venueId}/gallery`, formData);
        setPhotos((prev) => [...prev, res.data]);
      }
      toast.success(t("admin_venue_edit:photos_uploaded", { count: files.length, defaultValue: `${files.length} photo(s) uploaded` }));
    } catch (err) { toast.error(err.response?.data?.detail || t("admin_venue_edit:upload_failed", { defaultValue: "Upload failed" })); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/venues/${venueId}/gallery/${deleteTarget}`);
      setPhotos(photos.filter((p) => p.photo_id !== deleteTarget));
      toast.success(t("admin_venue_edit:photo_deleted", { defaultValue: "Photo deleted" }));
    } catch { toast.error(t("admin_venue_edit:photo_delete_failed", { defaultValue: "Failed to delete photo" })); }
    finally { setDeleteTarget(null); }
  };

  const handleSetCover = async (photoId) => {
    setSettingCover(photoId);
    try {
      const res = await axios.put(`${API}/admin/venues/${venueId}/gallery/${photoId}/set-cover`);
      const newOrder = res.data.order;
      setPhotos(prev => {
        const map = Object.fromEntries(prev.map(p => [p.photo_id, p]));
        return newOrder.map((pid, idx) => ({ ...map[pid], sort_order: idx })).filter(Boolean);
      });
      toast.success(t("admin_venue_edit:cover_updated", { defaultValue: "Cover photo updated" }));
    } catch { toast.error(t("admin_venue_edit:cover_failed", { defaultValue: "Failed to set cover" })); }
    finally { setSettingCover(null); }
  };

  const movePhoto = async (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= photos.length) return;
    const reordered = [...photos];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setPhotos(reordered);
    try {
      await axios.put(`${API}/admin/venues/${venueId}/gallery/reorder`, { order: reordered.map((p) => p.photo_id) });
    } catch { toast.error(t("admin_venue_edit:order_failed", { defaultValue: "Failed to save order" })); fetchPhotos(); }
  };

  const [dragIdx, setDragIdx] = useState(null);
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const reordered = [...photos];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    setPhotos(reordered);
    setDragIdx(null);
    try { await axios.put(`${API}/admin/venues/${venueId}/gallery/reorder`, { order: reordered.map((p) => p.photo_id) }); }
    catch { toast.error(t("admin_venue_edit:order_failed", { defaultValue: "Failed to save order" })); fetchPhotos(); }
  };

  if (loading) return <div className="animate-pulse bg-muted h-40 rounded-xl" />;

  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="gallery-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
          <ImagePlus className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_gallery", { defaultValue: "Photo Gallery" })}
          <span className="text-sm text-muted-foreground font-normal">({photos.length}/100)</span>
        </h2>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="gallery-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? t("admin_venue_edit:uploading", { defaultValue: "Uploading..." }) : t("admin_venue_edit:upload_photos", { defaultValue: "Upload Photos" })}
          </Button>
        </div>
      </div>
      {photos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-border rounded-lg">
          <ImagePlus className="h-10 w-10 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm">{t("admin_venue_edit:no_photos", { defaultValue: "No photos yet. Upload images to build the gallery." })}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo, idx) => (
            <div
              key={photo.photo_id}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              className={`relative group rounded-lg overflow-hidden border ${idx === 0 ? "border-primary border-2" : "border-border"} aspect-square bg-muted ${dragIdx === idx ? "opacity-50" : ""}`}
              data-testid={`gallery-photo-${photo.photo_id}`}
            >
              <img
                src={photo.url ? `${API.replace('/api', '')}${photo.url}` : ""}
                alt={photo.title || photo.filename}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23eee' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%23999' font-size='12'%3ENo img%3C/text%3E%3C/svg%3E"; }}
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1.5 flex items-center justify-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity sm:inset-0 sm:bg-black/0 sm:group-hover:bg-black/40 sm:p-0">
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" onClick={() => movePhoto(idx, -1)} disabled={idx === 0} title={t("admin_venue_edit:move_left", { defaultValue: "Move left" })} data-testid={`gallery-move-up-${photo.photo_id}`}><ChevronUp className="h-3.5 w-3.5" /></Button>
                {idx !== 0 && (
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-[10px]" onClick={() => handleSetCover(photo.photo_id)} disabled={settingCover === photo.photo_id} data-testid={`gallery-set-cover-${photo.photo_id}`} title={t("admin_venue_edit:set_as_cover", { defaultValue: "Set as cover" })}>
                    {settingCover === photo.photo_id ? <Loader2 className="h-3 w-3 animate-spin" /> : t("admin_venue_edit:cover", { defaultValue: "Cover" })}
                  </Button>
                )}
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" onClick={() => setDeleteTarget(photo.photo_id)} data-testid={`gallery-delete-${photo.photo_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                <Button size="sm" variant="secondary" className="h-7 w-7 p-0" onClick={() => movePhoto(idx, 1)} disabled={idx === photos.length - 1} title={t("admin_venue_edit:move_right", { defaultValue: "Move right" })} data-testid={`gallery-move-down-${photo.photo_id}`}><ChevronDown className="h-3.5 w-3.5" /></Button>
              </div>
              {idx === 0 && <span className="absolute bottom-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded font-medium">{t("admin_venue_edit:cover", { defaultValue: "Cover" })}</span>}
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_venue_edit:delete_photo_title", { defaultValue: "Delete Photo" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin_venue_edit:delete_photo_desc", { defaultValue: "Are you sure you want to delete this photo? This action cannot be undone." })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gallery-delete-cancel">{t("admin_venue_edit:cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="gallery-delete-confirm">{t("admin_venue_edit:delete", { defaultValue: "Delete" })}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/* ============== AVAILABILITY OVERRIDES ============== */
function AvailabilityOverrides({ venueId }) {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newOverride, setNewOverride] = useState({ date: "", is_closed: true, custom_open_time: "", custom_close_time: "", note: "" });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/venues/${venueId}/availability-overrides`)
      .then((res) => setOverrides(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [venueId]);

  const handleAdd = async () => {
    if (!newOverride.date) { toast.error(t("admin_venue_edit:date_required", { defaultValue: "Date is required" })); return; }
    setAdding(true);
    try {
      const payload = { date: newOverride.date, is_closed: newOverride.is_closed, note: newOverride.note };
      if (!newOverride.is_closed) {
        payload.custom_open_time = newOverride.custom_open_time || null;
        payload.custom_close_time = newOverride.custom_close_time || null;
      }
      const res = await axios.post(`${API}/admin/venues/${venueId}/availability-overrides`, payload);
      setOverrides([...overrides, res.data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewOverride({ date: "", is_closed: true, custom_open_time: "", custom_close_time: "", note: "" });
      setShowAdd(false);
      toast.success(t("admin_venue_edit:override_added", { defaultValue: "Override added" }));
    } catch (err) { toast.error(err.response?.data?.detail || t("admin_venue_edit:override_add_failed", { defaultValue: "Failed to add override" })); }
    finally { setAdding(false); }
  };

  const handleDelete = async (overrideId) => {
    try {
      await axios.delete(`${API}/admin/venues/${venueId}/availability-overrides/${overrideId}`);
      setOverrides(overrides.filter(o => o.override_id !== overrideId));
      toast.success(t("admin_venue_edit:override_removed", { defaultValue: "Override removed" }));
    } catch { toast.error(t("admin_venue_edit:override_delete_failed", { defaultValue: "Failed to delete override" })); }
  };

  if (loading) return <div className="animate-pulse bg-muted h-32 rounded-xl" />;

  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="availability-overrides-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
          <CalendarOff className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_overrides", { defaultValue: "Availability Overrides" })}
          <span className="text-sm text-muted-foreground font-normal">({overrides.length})</span>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-override-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("admin_venue_edit:add_override", { defaultValue: "Add Override" })}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-3" data-testid="add-override-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("admin_venue_edit:date", { defaultValue: "Date" })} *</Label><Input type="date" value={newOverride.date} onChange={(e) => setNewOverride({ ...newOverride, date: e.target.value })} className="mt-1" data-testid="override-date" /></div>
            <div><Label>{t("admin_venue_edit:note", { defaultValue: "Note" })}</Label><Input value={newOverride.note} onChange={(e) => setNewOverride({ ...newOverride, note: e.target.value })} className="mt-1" placeholder={t("admin_venue_edit:note_placeholder", { defaultValue: "e.g. Holiday, Private Event" })} data-testid="override-note" /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={newOverride.is_closed} onChange={(e) => setNewOverride({ ...newOverride, is_closed: e.target.checked })} className="rounded border-border" />
            <span className="text-sm">{t("admin_venue_edit:closed_all_day", { defaultValue: "Closed all day" })}</span>
          </label>
          {!newOverride.is_closed && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("admin_venue_edit:custom_open", { defaultValue: "Custom Open" })}</Label><Input type="time" value={newOverride.custom_open_time} onChange={(e) => setNewOverride({ ...newOverride, custom_open_time: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("admin_venue_edit:custom_close", { defaultValue: "Custom Close" })}</Label><Input type="time" value={newOverride.custom_close_time} onChange={(e) => setNewOverride({ ...newOverride, custom_close_time: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          <Button size="sm" onClick={handleAdd} disabled={adding} data-testid="override-submit">
            {adding ? t("admin_venue_edit:adding", { defaultValue: "Adding..." }) : t("admin_venue_edit:add_override", { defaultValue: "Add Override" })}
          </Button>
        </div>
      )}

      {overrides.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">{t("admin_venue_edit:no_overrides", { defaultValue: "No special date overrides. Regular opening hours apply." })}</p>
      ) : (
        <div className="space-y-2">
          {overrides.map((o) => (
            <div key={o.override_id} className="flex items-center justify-between bg-muted/40 rounded-lg px-4 py-2.5" data-testid={`override-${o.override_id}`}>
              <div>
                <span className="font-medium text-sm text-foreground">{o.date}</span>
                {o.is_closed ? (
                  <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{t("admin_venue_edit:closed", { defaultValue: "Closed" })}</span>
                ) : (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {o.custom_open_time || "?"} - {o.custom_close_time || "?"}
                  </span>
                )}
                {o.note && <span className="ml-2 text-xs text-muted-foreground">{o.note}</span>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(o.override_id)} data-testid={`override-delete-${o.override_id}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ============== MENU ITEMS MANAGER ============== */
function MenuManager({ venueId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", name_el: "", description: "", description_el: "", price: "", category: "", category_el: "", dietary_tags: [] });
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dietaryTags, setDietaryTags] = useState([]);

  const labelForTag = (tag) =>
    (lang === "el" && tag.name_el) || t(`dietary_tags.${tag.slug}`, { defaultValue: tag.name });

  useEffect(() => {
    axios.get(`${API}/admin/venues/${venueId}/menu`)
      .then(res => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/taxonomy/dietary-tags`)
      .then(res => setDietaryTags(res.data || []))
      .catch(() => setDietaryTags([]));
  }, [venueId]);

  const handleAdd = async () => {
    if (!newItem.name) { toast.error(t("admin_venue_edit:menu_name_required", { defaultValue: "Name is required" })); return; }
    setAdding(true);
    try {
      const payload = { ...newItem, dietary_tags: newItem.dietary_tags };
      if (payload.price) payload.price = parseFloat(payload.price);
      else delete payload.price;
      const res = await axios.post(`${API}/admin/venues/${venueId}/menu`, payload);
      setItems([...items, res.data]);
      setNewItem({ name: "", name_el: "", description: "", description_el: "", price: "", category: "", category_el: "", dietary_tags: [] });
      setShowAdd(false);
      toast.success(t("admin_venue_edit:menu_item_added", { defaultValue: "Menu item added" }));
    } catch (err) { toast.error(err.response?.data?.detail || t("admin_venue_edit:menu_item_add_failed", { defaultValue: "Failed to add item" })); }
    finally { setAdding(false); }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    try {
      const payload = { name: editingItem.name, name_el: editingItem.name_el || "", description: editingItem.description, description_el: editingItem.description_el || "", category: editingItem.category, category_el: editingItem.category_el || "", dietary_tags: editingItem.dietary_tags || [] };
      if (editingItem.price !== "" && editingItem.price !== null) payload.price = parseFloat(editingItem.price);
      const res = await axios.put(`${API}/admin/venues/${venueId}/menu/${editingItem.item_id}`, payload);
      setItems(items.map(i => i.item_id === editingItem.item_id ? res.data : i));
      setEditingItem(null);
      toast.success(t("admin_venue_edit:menu_item_updated", { defaultValue: "Item updated" }));
    } catch { toast.error(t("admin_venue_edit:menu_item_update_failed", { defaultValue: "Failed to update" })); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/venues/${venueId}/menu/${deleteTarget}`);
      setItems(items.filter(i => i.item_id !== deleteTarget));
      toast.success(t("admin_venue_edit:menu_item_deleted", { defaultValue: "Item deleted" }));
    } catch { toast.error(t("admin_venue_edit:menu_item_delete_failed", { defaultValue: "Failed to delete" })); }
    finally { setDeleteTarget(null); }
  };

  const handleImageUpload = async (itemId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/venues/${venueId}/menu/${itemId}/image`, formData);
      setItems(items.map(i => i.item_id === itemId ? { ...i, image_url: res.data.image_url } : i));
      toast.success(t("admin_venue_edit:image_uploaded", { defaultValue: "Image uploaded" }));
    } catch { toast.error(t("admin_venue_edit:image_upload_failed", { defaultValue: "Failed to upload image" })); }
  };

  if (loading) return <div className="animate-pulse bg-muted h-40 rounded-xl" />;

  const categories = [...new Set(items.map(i => i.category).filter(Boolean))];

  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="menu-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
          <UtensilsCrossed className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_menu", { defaultValue: "Menu Items" })}
          <span className="text-sm text-muted-foreground font-normal">({items.length})</span>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-menu-item-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("admin_venue_edit:add_item", { defaultValue: "Add Item" })}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-3" data-testid="add-menu-form">
          <BilingualInput
            label={t("admin_venue_edit:menu_name", { defaultValue: "Name" }) + " *"}
            value={newItem.name}
            valueEl={newItem.name_el}
            onChange={e => setNewItem({ ...newItem, name: e.target.value })}
            onChangeEl={e => setNewItem({ ...newItem, name_el: e.target.value })}
            placeholder={t("admin_venue_edit:menu_name_placeholder", { defaultValue: "e.g. Grilled Halloumi" })}
            data-testid="menu-item-name"
          />
          <BilingualInput
            label={t("admin_venue_edit:menu_category", { defaultValue: "Category" })}
            value={newItem.category}
            valueEl={newItem.category_el}
            onChange={e => setNewItem({ ...newItem, category: e.target.value })}
            onChangeEl={e => setNewItem({ ...newItem, category_el: e.target.value })}
            placeholder={t("admin_venue_edit:menu_category_placeholder", { defaultValue: "e.g. Starters" })}
            data-testid="menu-item-category"
          />
          {categories.length > 0 && <datalist id="category-suggestions">{categories.map(c => <option key={c} value={c} />)}</datalist>}
          <BilingualTextarea
            label={t("admin_venue_edit:menu_description", { defaultValue: "Description" })}
            value={newItem.description}
            valueEl={newItem.description_el}
            onChange={e => setNewItem({ ...newItem, description: e.target.value })}
            onChangeEl={e => setNewItem({ ...newItem, description_el: e.target.value })}
            placeholder={t("admin_venue_edit:menu_description_placeholder", { defaultValue: "Brief description" })}
            rows={2}
            data-testid="menu-item-desc"
          />
          <div><Label>{t("admin_venue_edit:menu_price", { defaultValue: "Price (EUR)" })}</Label><Input type="number" step="0.01" min="0" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} className="mt-1 w-32" placeholder="0.00" data-testid="menu-item-price" /></div>
          <div>
            <Label>{t("admin_venue_edit:dietary_tags", { defaultValue: "Dietary Tags" })}</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {dietaryTags.map(tag => (
                <button key={tag.slug} type="button" onClick={() => setNewItem({ ...newItem, dietary_tags: newItem.dietary_tags.includes(tag.slug) ? newItem.dietary_tags.filter(x => x !== tag.slug) : [...newItem.dietary_tags, tag.slug] })}
                  className={`px-2 py-0.5 rounded-full text-xs transition-colors ${newItem.dietary_tags.includes(tag.slug) ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                >{labelForTag(tag)}</button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={handleAdd} disabled={adding} data-testid="menu-item-submit">
            {adding ? t("admin_venue_edit:adding", { defaultValue: "Adding..." }) : t("admin_venue_edit:add_item", { defaultValue: "Add Item" })}
          </Button>
        </div>
      )}

      {items.length === 0 && !showAdd ? (
        <p className="text-sm text-muted-foreground text-center py-6">{t("admin_venue_edit:no_menu_items", { defaultValue: "No menu items yet. Add dishes, drinks, or specials." })}</p>
      ) : (
        <div className="space-y-2">
          {categories.length > 0 && categories.map(cat => (
            <div key={cat}>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-4 mb-2">{cat}</h3>
              {items.filter(i => i.category === cat).map(item => (
                <MenuItemRow key={item.item_id} item={item} dietaryTags={dietaryTags} onEdit={() => setEditingItem({ ...item })} onDelete={() => setDeleteTarget(item.item_id)} onImageUpload={handleImageUpload} />
              ))}
            </div>
          ))}
          {items.filter(i => !i.category).length > 0 && (
            <div>
              {categories.length > 0 && <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-4 mb-2">{t("admin_venue_edit:other_category", { defaultValue: "Other" })}</h3>}
              {items.filter(i => !i.category).map(item => (
                <MenuItemRow key={item.item_id} item={item} dietaryTags={dietaryTags} onEdit={() => setEditingItem({ ...item })} onDelete={() => setDeleteTarget(item.item_id)} onImageUpload={handleImageUpload} />
              ))}
            </div>
          )}
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingItem(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="edit-menu-dialog">
            <h3 className="text-lg font-medium">{t("admin_venue_edit:edit_menu_item", { defaultValue: "Edit Menu Item" })}</h3>
            <BilingualInput
              label={t("admin_venue_edit:menu_name", { defaultValue: "Name" }) + " *"}
              value={editingItem.name}
              valueEl={editingItem.name_el || ""}
              onChange={e => setEditingItem({ ...editingItem, name: e.target.value })}
              onChangeEl={e => setEditingItem({ ...editingItem, name_el: e.target.value })}
            />
            <BilingualTextarea
              label={t("admin_venue_edit:menu_description", { defaultValue: "Description" })}
              value={editingItem.description || ""}
              valueEl={editingItem.description_el || ""}
              onChange={e => setEditingItem({ ...editingItem, description: e.target.value })}
              onChangeEl={e => setEditingItem({ ...editingItem, description_el: e.target.value })}
              rows={2}
            />
            <BilingualInput
              label={t("admin_venue_edit:menu_category", { defaultValue: "Category" })}
              value={editingItem.category || ""}
              valueEl={editingItem.category_el || ""}
              onChange={e => setEditingItem({ ...editingItem, category: e.target.value })}
              onChangeEl={e => setEditingItem({ ...editingItem, category_el: e.target.value })}
            />
            <div><Label>{t("admin_venue_edit:menu_price", { defaultValue: "Price (EUR)" })}</Label><Input type="number" step="0.01" value={editingItem.price || ""} onChange={e => setEditingItem({ ...editingItem, price: e.target.value })} className="mt-1 w-32" /></div>
            <div>
              <Label>{t("admin_venue_edit:dietary_tags", { defaultValue: "Dietary Tags" })}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {dietaryTags.map(tag => (
                  <button key={tag.slug} type="button" onClick={() => setEditingItem({ ...editingItem, dietary_tags: (editingItem.dietary_tags || []).includes(tag.slug) ? editingItem.dietary_tags.filter(x => x !== tag.slug) : [...(editingItem.dietary_tags || []), tag.slug] })}
                    className={`px-2 py-0.5 rounded-full text-xs transition-colors ${(editingItem.dietary_tags || []).includes(tag.slug) ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                  >{labelForTag(tag)}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditingItem(null)}>{t("admin_venue_edit:cancel", { defaultValue: "Cancel" })}</Button>
              <Button size="sm" onClick={handleUpdate} data-testid="edit-menu-save">{t("admin_venue_edit:save", { defaultValue: "Save" })}</Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_venue_edit:delete_menu_item_title", { defaultValue: "Delete Menu Item" })}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin_venue_edit:delete_menu_item_desc", { defaultValue: "Are you sure you want to delete this item? This action cannot be undone." })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin_venue_edit:cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="menu-delete-confirm">{t("admin_venue_edit:delete", { defaultValue: "Delete" })}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function MenuItemRow({ item, dietaryTags = [], onEdit, onDelete, onImageUpload }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const imgInputRef = useRef(null);
  const imgUrl = item.image_url
    ? (item.image_url.startsWith("http") ? item.image_url : `${API.replace('/api', '')}${item.image_url}`)
    : null;
  const displayName = (lang === "el" && item.name_el) || item.name;
  const displayDesc = (lang === "el" && item.description_el) || item.description;
  const displayCat = (lang === "el" && item.category_el) || item.category;
  const tagLabel = (slug) => {
    const dt = dietaryTags.find(x => x.slug === slug);
    if (dt) return (lang === "el" && dt.name_el) || t(`dietary_tags.${dt.slug}`, { defaultValue: dt.name });
    return t(`dietary_tags.${slug}`, { defaultValue: slug });
  };

  return (
    <div className="flex items-center gap-3 bg-muted/40 rounded-lg px-4 py-3" data-testid={`menu-item-${item.item_id}`}>
      <div className="w-14 h-14 rounded-lg bg-muted overflow-hidden shrink-0 border border-border cursor-pointer" onClick={() => imgInputRef.current?.click()}>
        {imgUrl ? (
          <img src={imgUrl} alt={displayName} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ImagePlus className="h-5 w-5" />
          </div>
        )}
        <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) onImageUpload(item.item_id, e.target.files[0]); }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-foreground truncate">{displayName}</span>
          {displayCat && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{displayCat}</span>}
        </div>
        {displayDesc && <p className="text-xs text-muted-foreground truncate">{displayDesc}</p>}
        {item.dietary_tags?.length > 0 && (
          <div className="flex gap-1 mt-0.5 flex-wrap">
            {item.dietary_tags.map(tag => <span key={tag} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">{tagLabel(tag)}</span>)}
          </div>
        )}
      </div>
      {item.price != null && (
        <span className="text-sm font-semibold text-foreground shrink-0">{"\u20AC"}{Number(item.price).toFixed(2)}</span>
      )}
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit} data-testid={`menu-edit-${item.item_id}`}><Edit className="h-3.5 w-3.5 text-blue-500" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete} data-testid={`menu-delete-${item.item_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
      </div>
    </div>
  );
}

/* ============== PROMOTIONS / SPECIAL OFFERS ============== */
function PromotionsManager({ venueId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newPromo, setNewPromo] = useState({ title: "", title_el: "", description: "", description_el: "", discount_text: "", discount_text_el: "", start_date: "", end_date: "", start_time: "", end_time: "" });
  const [adding, setAdding] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/venues/${venueId}/promotions`)
      .then(res => setPromos(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [venueId]);

  const handleAdd = async () => {
    if (!newPromo.title || !newPromo.start_date || !newPromo.end_date) { toast.error(t("admin_venue_edit:promo_required", { defaultValue: "Title, start and end dates required" })); return; }
    setAdding(true);
    try {
      const payload = { ...newPromo };
      if (!payload.start_time) delete payload.start_time;
      if (!payload.end_time) delete payload.end_time;
      const res = await axios.post(`${API}/admin/venues/${venueId}/promotions`, payload);
      setPromos([res.data, ...promos]);
      setNewPromo({ title: "", title_el: "", description: "", description_el: "", discount_text: "", discount_text_el: "", start_date: "", end_date: "", start_time: "", end_time: "" });
      setShowAdd(false);
      toast.success(t("admin_venue_edit:promo_created", { defaultValue: "Promotion created" }));
    } catch (err) { toast.error(err.response?.data?.detail || t("admin_venue_edit:promo_create_failed", { defaultValue: "Failed to create" })); }
    finally { setAdding(false); }
  };

  const handleUpdate = async () => {
    if (!editingPromo) return;
    try {
      const payload = { title: editingPromo.title, title_el: editingPromo.title_el || "", description: editingPromo.description, description_el: editingPromo.description_el || "", discount_text: editingPromo.discount_text, discount_text_el: editingPromo.discount_text_el || "", start_date: editingPromo.start_date, end_date: editingPromo.end_date, is_active: editingPromo.is_active };
      if (editingPromo.start_time) payload.start_time = editingPromo.start_time;
      if (editingPromo.end_time) payload.end_time = editingPromo.end_time;
      const res = await axios.put(`${API}/admin/venues/${venueId}/promotions/${editingPromo.promo_id}`, payload);
      setPromos(promos.map(p => p.promo_id === editingPromo.promo_id ? res.data : p));
      setEditingPromo(null);
      toast.success(t("admin_venue_edit:promo_updated", { defaultValue: "Promotion updated" }));
    } catch { toast.error(t("admin_venue_edit:promo_update_failed", { defaultValue: "Failed to update" })); }
  };

  const handleDelete = async (promoId) => {
    try {
      await axios.delete(`${API}/admin/venues/${venueId}/promotions/${promoId}`);
      setPromos(promos.filter(p => p.promo_id !== promoId));
      toast.success(t("admin_venue_edit:promo_deleted", { defaultValue: "Promotion deleted" }));
    } catch { toast.error(t("admin_venue_edit:promo_delete_failed", { defaultValue: "Failed to delete" })); }
  };

  const isExpired = (endDate) => new Date(endDate) < new Date();

  if (loading) return <div className="animate-pulse bg-muted h-32 rounded-xl" />;

  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="promotions-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-foreground flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-primary" /> {t("admin_venue_edit:section_promotions", { defaultValue: "Special Offers" })}
          <span className="text-sm text-muted-foreground font-normal">({promos.length})</span>
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(!showAdd)} data-testid="add-promo-btn">
          <Plus className="h-4 w-4 mr-1" /> {t("admin_venue_edit:add_offer", { defaultValue: "Add Offer" })}
        </Button>
      </div>

      {showAdd && (
        <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-3" data-testid="add-promo-form">
          <BilingualInput
            label={t("admin_venue_edit:promo_title", { defaultValue: "Title" }) + " *"}
            value={newPromo.title}
            valueEl={newPromo.title_el}
            onChange={e => setNewPromo({ ...newPromo, title: e.target.value })}
            onChangeEl={e => setNewPromo({ ...newPromo, title_el: e.target.value })}
            placeholder={t("admin_venue_edit:promo_title_placeholder", { defaultValue: "e.g. Happy Hour" })}
            data-testid="promo-title"
          />
          <BilingualTextarea
            label={t("admin_venue_edit:promo_description", { defaultValue: "Description" })}
            value={newPromo.description}
            valueEl={newPromo.description_el}
            onChange={e => setNewPromo({ ...newPromo, description: e.target.value })}
            onChangeEl={e => setNewPromo({ ...newPromo, description_el: e.target.value })}
            placeholder={t("admin_venue_edit:promo_description_placeholder", { defaultValue: "Details about the offer" })}
            rows={2}
          />
          <BilingualInput
            label={t("admin_venue_edit:promo_discount", { defaultValue: "Discount" })}
            value={newPromo.discount_text}
            valueEl={newPromo.discount_text_el}
            onChange={e => setNewPromo({ ...newPromo, discount_text: e.target.value })}
            onChangeEl={e => setNewPromo({ ...newPromo, discount_text_el: e.target.value })}
            placeholder={t("admin_venue_edit:promo_discount_placeholder", { defaultValue: "e.g. 20% off cocktails" })}
            data-testid="promo-discount"
          />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("admin_venue_edit:start_date", { defaultValue: "Start Date" })} *</Label><Input type="date" value={newPromo.start_date} onChange={e => setNewPromo({ ...newPromo, start_date: e.target.value })} className="mt-1" data-testid="promo-start" /></div>
            <div><Label>{t("admin_venue_edit:end_date", { defaultValue: "End Date" })} *</Label><Input type="date" value={newPromo.end_date} onChange={e => setNewPromo({ ...newPromo, end_date: e.target.value })} className="mt-1" data-testid="promo-end" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("admin_venue_edit:time_from", { defaultValue: "Time From (optional)" })}</Label><Input type="time" value={newPromo.start_time} onChange={e => setNewPromo({ ...newPromo, start_time: e.target.value })} className="mt-1" /></div>
            <div><Label>{t("admin_venue_edit:time_until", { defaultValue: "Time Until (optional)" })}</Label><Input type="time" value={newPromo.end_time} onChange={e => setNewPromo({ ...newPromo, end_time: e.target.value })} className="mt-1" /></div>
          </div>
          <Button size="sm" onClick={handleAdd} disabled={adding} data-testid="promo-submit">
            {adding ? t("admin_venue_edit:creating", { defaultValue: "Creating..." }) : t("admin_venue_edit:create_offer", { defaultValue: "Create Offer" })}
          </Button>
        </div>
      )}

      {promos.length === 0 && !showAdd ? (
        <p className="text-sm text-muted-foreground text-center py-4">{t("admin_venue_edit:no_promotions", { defaultValue: "No special offers. Create promotions to attract more customers." })}</p>
      ) : (
        <div className="space-y-2">
          {promos.map(promo => (
            <div key={promo.promo_id} className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 ${isExpired(promo.end_date) ? "bg-muted/30 opacity-60" : "bg-muted/40"}`} data-testid={`promo-${promo.promo_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-foreground">{(lang === "el" && promo.title_el) || promo.title}</span>
                  {(promo.discount_text || promo.discount_text_el) && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-medium">{(lang === "el" && promo.discount_text_el) || promo.discount_text}</span>}
                  {!promo.is_active && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{t("admin_venue_edit:inactive", { defaultValue: "Inactive" })}</span>}
                  {isExpired(promo.end_date) && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{t("admin_venue_edit:expired", { defaultValue: "Expired" })}</span>}
                </div>
                {(promo.description || promo.description_el) && <p className="text-xs text-muted-foreground mt-0.5">{(lang === "el" && promo.description_el) || promo.description}</p>}
                <p className="text-xs text-muted-foreground mt-0.5">
                  {promo.start_date} {t("admin_venue_edit:to", { defaultValue: "to" })} {promo.end_date}
                  {promo.start_time && promo.end_time && ` \u2022 ${promo.start_time}-${promo.end_time}`}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditingPromo({ ...promo })} data-testid={`promo-edit-${promo.promo_id}`}><Edit className="h-3.5 w-3.5 text-blue-500" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(promo.promo_id)} data-testid={`promo-delete-${promo.promo_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingPromo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingPromo(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()} data-testid="edit-promo-dialog">
            <h3 className="text-lg font-medium">{t("admin_venue_edit:edit_special_offer", { defaultValue: "Edit Special Offer" })}</h3>
            <BilingualInput
              label={t("admin_venue_edit:promo_title", { defaultValue: "Title" }) + " *"}
              value={editingPromo.title}
              valueEl={editingPromo.title_el || ""}
              onChange={e => setEditingPromo({ ...editingPromo, title: e.target.value })}
              onChangeEl={e => setEditingPromo({ ...editingPromo, title_el: e.target.value })}
            />
            <BilingualTextarea
              label={t("admin_venue_edit:promo_description", { defaultValue: "Description" })}
              value={editingPromo.description || ""}
              valueEl={editingPromo.description_el || ""}
              onChange={e => setEditingPromo({ ...editingPromo, description: e.target.value })}
              onChangeEl={e => setEditingPromo({ ...editingPromo, description_el: e.target.value })}
              rows={2}
            />
            <BilingualInput
              label={t("admin_venue_edit:promo_discount", { defaultValue: "Discount" })}
              value={editingPromo.discount_text || ""}
              valueEl={editingPromo.discount_text_el || ""}
              onChange={e => setEditingPromo({ ...editingPromo, discount_text: e.target.value })}
              onChangeEl={e => setEditingPromo({ ...editingPromo, discount_text_el: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("admin_venue_edit:start_date", { defaultValue: "Start Date" })}</Label><Input type="date" value={editingPromo.start_date} onChange={e => setEditingPromo({ ...editingPromo, start_date: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("admin_venue_edit:end_date", { defaultValue: "End Date" })}</Label><Input type="date" value={editingPromo.end_date} onChange={e => setEditingPromo({ ...editingPromo, end_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("admin_venue_edit:time_from_short", { defaultValue: "Time From" })}</Label><Input type="time" value={editingPromo.start_time || ""} onChange={e => setEditingPromo({ ...editingPromo, start_time: e.target.value })} className="mt-1" /></div>
              <div><Label>{t("admin_venue_edit:time_until_short", { defaultValue: "Time Until" })}</Label><Input type="time" value={editingPromo.end_time || ""} onChange={e => setEditingPromo({ ...editingPromo, end_time: e.target.value })} className="mt-1" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editingPromo.is_active} onChange={e => setEditingPromo({ ...editingPromo, is_active: e.target.checked })} className="rounded border-border" />
              <span className="text-sm">{t("admin_venue_edit:active", { defaultValue: "Active" })}</span>
            </label>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditingPromo(null)}>{t("admin_venue_edit:cancel", { defaultValue: "Cancel" })}</Button>
              <Button size="sm" onClick={handleUpdate} data-testid="edit-promo-save">{t("admin_venue_edit:save", { defaultValue: "Save" })}</Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============== VENUE PLAN PANEL (Phase 2) ============== */
/**
 * Sidebar panel that lets the admin see and switch the venue's active plan.
 * Hits PUT /api/admin/venues/{id}/plan which also flips the cached
 * `bookings_enabled` flag based on the plan's feature toggles.
 */
function VenuePlanPanel({ venueId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [plans, setPlans] = useState([]);
  const [activeKey, setActiveKey] = useState("free_listing");
  const [activePlan, setActivePlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Fire both requests in parallel but isolate failures so a 404 on
        // /plan (e.g., a brand-new venue with no plan_key yet) doesn't blank
        // out the whole panel — we still want to render the dropdown.
        const [plansRes, currentRes] = await Promise.all([
          axios.get(`${API}/admin/venue-plans`).catch(() => ({ data: [] })),
          axios.get(`${API}/venues/${venueId}/plan`).catch(() => ({ data: null })),
        ]);
        setPlans(plansRes.data || []);
        setActivePlan(currentRes.data);
        setActiveKey(currentRes.data?.plan_key || "free_listing");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [venueId]);

  const handleAssign = async (plan_key) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/venues/${venueId}/plan`, { plan_key });
      setActivePlan(res.data.plan);
      setActiveKey(plan_key);
      toast.success(
        t("admin_venue_edit:plan_assigned", {
          defaultValue: "Plan switched to {{name}}",
          name: (lang === "el" && res.data.plan?.name_el) || res.data.plan?.name || plan_key,
        }),
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <section className="bg-card rounded-xl border border-border p-6 animate-pulse h-32" />;
  }

  const enabledCount = activePlan
    ? Object.values(activePlan.feature_toggles || {}).filter(Boolean).length
    : 0;

  return (
    <section className="bg-card rounded-xl border border-border p-6" data-testid="admin-venue-plan-panel">
      <h2 className="text-lg font-medium text-foreground mb-1">
        {t("admin_venue_edit:section_plan", { defaultValue: "Plan & Feature Access" })}
      </h2>
      <p className="text-xs text-muted-foreground mb-4">
        {t("admin_venue_edit:section_plan_hint", {
          defaultValue: "Controls which features this venue can use. Switching plans is instant.",
        })}
      </p>

      <Label>{t("admin_venue_edit:field_plan", { defaultValue: "Active plan" })}</Label>
      <Select value={activeKey} onValueChange={handleAssign} disabled={saving}>
        <SelectTrigger className="mt-1" data-testid="admin-venue-plan-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {plans.map((p) => (
            <SelectItem key={p.plan_key} value={p.plan_key}>
              {(lang === "el" && p.name_el) || p.name} — €{p.price_monthly_eur}/mo
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {activePlan && (
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {t("admin_venue_edit:plan_features_on", { defaultValue: "Features enabled" })}
            </span>
            <span className="font-medium text-foreground">
              {enabledCount}/{Object.keys(activePlan.feature_toggles || {}).length}
            </span>
          </div>
          {(lang === "el" ? activePlan.description_el : activePlan.description) && (
            <p className="text-xs text-muted-foreground italic">
              {lang === "el" ? activePlan.description_el : activePlan.description}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

